import { createField, type ScalarField } from './grid.ts';

/**
 * Slope angle and aspect by Horn's method (3×3 weighted differences), ported from norway3d's
 * `lib/analysis/slope.ts`. Edges reuse the nearest in-grid neighbour.
 *
 * Aspect is the true compass bearing the slope faces (downhill direction): 0° = N, 90° = E.
 * The grid is UTM 33, whose north differs from true north away from 15°E, so the grid bearing
 * is corrected by `gridNorthBearing` (see terrain/projections.ts). Flat cells get aspect NaN.
 */
export interface Topography {
  /** Slope angle, degrees. */
  slope: ScalarField;
  /** Aspect, degrees clockwise from north; NaN where flat. */
  aspect: ScalarField;
}

const RAD_TO_DEG = 180 / Math.PI;
const FLAT_GRADIENT = 1e-6;

export function computeTopography(elevation: ScalarField, gridNorthBearing = 0): Topography {
  const { spec, data } = elevation;
  const { cols, rows, cellSize } = spec;
  const slope = createField(spec);
  const aspect = createField(spec);

  const z = (c: number, r: number): number => {
    const cc = c < 0 ? 0 : c >= cols ? cols - 1 : c;
    const rr = r < 0 ? 0 : r >= rows ? rows - 1 : r;
    return data[rr * cols + cc]!;
  };

  const inv8 = 1 / (8 * cellSize);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const a = z(c - 1, r - 1);
      const b = z(c, r - 1);
      const cc = z(c + 1, r - 1);
      const d = z(c - 1, r);
      const f = z(c + 1, r);
      const g = z(c - 1, r + 1);
      const h = z(c, r + 1);
      const i = z(c + 1, r + 1);
      // x grows east. Row index grows south, so dz/dnorth = (north row − south row).
      const dzdx = (cc + 2 * f + i - (a + 2 * d + g)) * inv8;
      const dzdy = (a + 2 * b + cc - (g + 2 * h + i)) * inv8;
      const grad = Math.hypot(dzdx, dzdy);
      const idx = r * cols + c;
      slope.data[idx] = Math.atan(grad) * RAD_TO_DEG;
      if (grad < FLAT_GRADIENT) {
        aspect.data[idx] = NaN;
      } else {
        // Downhill direction is −gradient; bearing measured clockwise from north.
        const bearing = (Math.atan2(-dzdx, -dzdy) * RAD_TO_DEG + gridNorthBearing) % 360;
        aspect.data[idx] = bearing < 0 ? bearing + 360 : bearing;
      }
    }
  }
  return { slope, aspect };
}
